import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Loader2, MessageSquareWarning, PencilLine } from 'lucide-react';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export const ReviewBanner = ({ courseId, refreshKey }) => {
  const { api } = useAuth();
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!courseId) return;
    api.get(`/course-review/courses/${courseId}/status`).then((r) => setS(r.data)).catch(() => setS(null));
  }, [api, courseId, refreshKey]);
  if (!s) return null;
  const base = 'flex flex-wrap items-center gap-2 px-6 py-2.5 border-b text-sm';
  if (s.review_status === 'pending') {
    return (
      <div className={`${base} bg-amber-50 border-amber-200 text-amber-800`} data-testid="review-banner-pending">
        <Clock className="h-4 w-4" /> Waiting for Super Admin approval (submitted {fmt(s.review_submitted_at)}).
        {s.status === 'published' && ' Learners keep seeing the current live version until it is approved.'}
      </div>
    );
  }
  if (s.review_status === 'rejected') {
    return (
      <div className={`${base} bg-red-50 border-red-200 text-red-800`} data-testid="review-banner-rejected">
        <MessageSquareWarning className="h-4 w-4" /> Changes requested: <span className="font-medium">{s.review_comment}</span>
      </div>
    );
  }
  if (s.status === 'published' && s.has_pending_changes) {
    return (
      <div className={`${base} bg-sky-50 border-sky-200 text-sky-800`} data-testid="review-banner-unpublished-changes">
        <PencilLine className="h-4 w-4" /> You have edits that aren't live yet. Learners see the version published {fmt(s.live_published_at)}.
      </div>
    );
  }
  return null;
};

export const SubmitReviewDialog = ({ open, courseId, onClose, onSubmitted }) => {
  const { api } = useAuth();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setNote(''); }, [open]);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/course-review/courses/${courseId}/submit`, { note });
      toast.success('Submitted for approval — the Super Admin has been notified');
      onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit for approval');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="submit-review-dialog">
        <DialogHeader>
          <DialogTitle>Submit for approval</DialogTitle>
          <DialogDescription>A Super Admin will review this course before it goes live.</DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional: what changed?" data-testid="submit-review-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="submit-review-confirm">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
